import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  FileBadge,
  Flame,
  Handshake,
  HardHat,
  Headphones,
  Linkedin,
  Mail,
  Network,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../../../components/layout/Container';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Reveal } from '../../../components/motion/Reveal';
import { BreadcrumbListLd } from '../../../components/seo/jsonld/BreadcrumbList';
import { toast } from '../../../components/ui/toast';
import { cn } from '../../../lib/cn';
import { ROUTES } from '@shared/constants/routes';
import type { Segment } from '@shared/constants/segments';
import type { Intent } from '@shared/constants/intents';

// ════════════════════════════════════════════════════════════════
// Configuration — 4 profils de partenaires
// ════════════════════════════════════════════════════════════════

type ToneKey = 'brand' | 'lavender' | 'sage' | 'accent';

interface PartnerProfile {
  id: 'artisan' | 'delegataire' | 'oblige' | 'apporteur';
  label: string;
  pill: string;
  hint: string;
  icon: LucideIcon;
  tone: ToneKey;
  highlight?: boolean;
  /** Segment back transmis à l'API leads */
  segment: Segment;
  /** Intent back transmis à l'API leads */
  intent: Intent;
  /** Label du champ « entité » */
  companyLabel: string;
  /** Champs spécifiques au profil */
  specificFields: SpecificField[];
  /** Placeholder du message libre */
  messagePlaceholder: string;
  /** Bandeau d'accroche au-dessus du formulaire */
  formEyebrow: string;
  /** Phrase rassurante sous le bouton submit */
  footnote: string;
}

interface SpecificField {
  name: string;
  label: string;
  hint?: string;
  placeholder?: string;
  type?: 'text' | 'tel' | 'url' | 'number';
  optional?: boolean;
}

const PARTNERS: PartnerProfile[] = [
  {
    id: 'artisan',
    label: 'Artisan RGE',
    pill: 'Mandat CEE · service phare',
    hint: 'Entreprise du bâtiment qualifiée RGE qui veut industrialiser ses chantiers CEE.',
    icon: HardHat,
    tone: 'accent',
    highlight: true,
    segment: 'pro',
    intent: 'cee',
    companyLabel: 'Raison sociale',
    specificFields: [
      {
        name: 'siret',
        label: 'SIRET',
        placeholder: '14 chiffres',
        hint: 'Vérification automatique de votre qualification RGE sur le registre officiel.',
      },
      {
        name: 'qualifications',
        label: 'Qualifications RGE actives',
        placeholder: 'QualiPAC, Qualibat 8611, Qualibois, Qualisol, Éco-Artisan…',
      },
      {
        name: 'volumeAnnuel',
        label: 'Volume de chantiers CEE / an (estimation)',
        placeholder: 'Ex. : 80 chantiers / an, dont 30 PAC air/eau',
        optional: true,
      },
    ],
    messagePlaceholder:
      'Présentez votre activité, vos zones d’intervention, vos qualifications principales et les fiches CEE déjà déposées (ou prévues).',
    formEyebrow: 'Devenir artisan partenaire — mandat CEE',
    footnote:
      "0 € d'avance de trésorerie · suivi temps réel des dossiers · pas de mandat exclusif.",
  },
  {
    id: 'delegataire',
    label: 'Délégataire CEE',
    pill: 'Délégation officielle CEE',
    hint: 'Délégataire référencé au registre officiel des CEE.',
    icon: Network,
    tone: 'lavender',
    segment: 'entreprise',
    intent: 'cee',
    companyLabel: 'Raison sociale',
    specificFields: [
      {
        name: 'siret',
        label: 'SIRET',
        placeholder: '14 chiffres',
      },
      {
        name: 'delegationRef',
        label: 'Référence de votre délégation officielle CEE',
        placeholder: 'Identifiant de délégation au registre officiel',
      },
      {
        name: 'volumeTwhc',
        label: 'Volume CEE recherché (TWhc / an, estimation)',
        placeholder: 'Ex. : 0,5 TWhc cumac / an',
        optional: true,
      },
    ],
    messagePlaceholder:
      'Précisez la nature de votre délégation, les fiches CEE prioritaires (résidentiel, tertiaire, agri, transport), votre périmètre géographique et vos contraintes documentaires.',
    formEyebrow: 'Partenariat délégataire CEE',
    footnote:
      'Sourcing qualifié · conformité décret du 25 mars 2022 · reporting trimestriel.',
  },
  {
    id: 'oblige',
    label: 'Obligé énergie',
    pill: 'Fournisseur soumis à obligation',
    hint: "Fournisseur d'énergie soumis à obligation CEE par l'administration en charge du dispositif.",
    icon: Flame,
    tone: 'brand',
    segment: 'entreprise',
    intent: 'cee',
    companyLabel: 'Société',
    specificFields: [
      {
        name: 'siret',
        label: 'SIRET',
        placeholder: '14 chiffres',
      },
      {
        name: 'typeEnergie',
        label: "Type d'énergie distribuée",
        placeholder: 'Électricité / gaz / fioul / GPL / chaleur / carburants',
      },
      {
        name: 'volumeRequis',
        label: 'Volume CEE à acquérir (TWhc / an, estimation)',
        placeholder: 'Ex. : 1,2 TWhc cumac / an',
        optional: true,
      },
    ],
    messagePlaceholder:
      "Décrivez votre obligation CEE en cours (volume, échéances, types de fiches prioritaires) et vos critères de sélection partenaires (qualité, traçabilité, reporting).",
    formEyebrow: "Partenariat obligé énergie",
    footnote:
      "Volumes traçables · contrôle qualité documentaire · CGV et mandat publiés en amont.",
  },
  {
    id: 'apporteur',
    label: "Apporteur d'affaires",
    pill: 'Prescripteur & recommandation',
    hint: "Structure en contact régulier avec des prospects (particuliers, pros, copros, collectivités).",
    icon: Handshake,
    tone: 'sage',
    segment: 'pro',
    intent: 'inconnu',
    companyLabel: 'Structure (cabinet, entreprise…)',
    specificFields: [
      {
        name: 'metier',
        label: "Votre activité principale",
        placeholder:
          'Consultant énergie, courtier assurance, syndic, AMO, agence immobilière…',
      },
      {
        name: 'audience',
        label: 'Vos clients/cibles habituels',
        placeholder:
          'Ex. : artisans BTP, copropriétés Île-de-France, PME industrielles…',
      },
      {
        name: 'volumeMensuel',
        label: 'Volume de leads transmissibles / mois (estimation)',
        placeholder: 'Ex. : 5 à 10 / mois',
        optional: true,
      },
    ],
    messagePlaceholder:
      "Décrivez votre activité, le profil-type des prospects que vous croisez et le mode de partenariat envisagé (recommandation, co-branding, intégration outil).",
    formEyebrow: "Programme apporteur d'affaires",
    footnote:
      'Contrat de mandat d’apport simple · rémunération versée au dossier abouti · aucun exclusif.',
  },
];

const TONE_STYLES: Record<
  ToneKey,
  {
    bg: string;
    ring: string;
    iconBg: string;
    iconText: string;
    ringActive: string;
  }
> = {
  brand: {
    bg: 'bg-brand-50/50',
    ring: 'ring-brand-200/60',
    iconBg: 'bg-brand-100',
    iconText: 'text-brand-700',
    ringActive: 'ring-brand-500/40',
  },
  lavender: {
    bg: 'bg-lavender-50/50',
    ring: 'ring-lavender-200/60',
    iconBg: 'bg-lavender-100',
    iconText: 'text-lavender-700',
    ringActive: 'ring-lavender-500/40',
  },
  sage: {
    bg: 'bg-sage-100/70',
    ring: 'ring-stone-200/60',
    iconBg: 'bg-sage-200/80',
    iconText: 'text-sage-500',
    ringActive: 'ring-sage-500/40',
  },
  accent: {
    bg: 'bg-accent-50/60',
    ring: 'ring-accent-200/70',
    iconBg: 'bg-accent-100',
    iconText: 'text-accent-800',
    ringActive: 'ring-accent-500/45',
  },
};

const NEXT_STEPS = [
  {
    n: '24 h',
    title: 'Premier contact',
    desc: 'Un référent partenaires vous rappelle pour qualifier votre demande et valider la pertinence du partenariat.',
  },
  {
    n: '5 j',
    title: 'Étude de dossier',
    desc: "Vérification des pièces (RGE, KBis, délégation officielle CEE, registres professionnels…). Aucun engagement de votre côté à cette étape.",
  },
  {
    n: '2 sem.',
    title: 'Cadrage juridique & économique',
    desc: 'Contrat-type, engagements réciproques, grille de rémunération communiquée par écrit avant toute signature.',
  },
  {
    n: '1 mois',
    title: 'Activation',
    desc: "Onboarding équipe, accès à l'espace partenaire, transmission des premiers dossiers ou opportunités.",
  },
];

// ════════════════════════════════════════════════════════════════
// État du formulaire
// ════════════════════════════════════════════════════════════════

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  company: string;
  /** champs dynamiques selon le profil */
  specific: Record<string, string>;
  message: string;
  consentRgpd: boolean;
  consentMarketing: boolean;
}

const INITIAL_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  company: '',
  specific: {},
  message: '',
  consentRgpd: false,
  consentMarketing: false,
};

function findProfile(id: string | null): PartnerProfile | null {
  if (!id) return null;
  return PARTNERS.find((p) => p.id === id) ?? null;
}

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export default function CandidaterPage() {
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ id: string } | null>(null);

  // Pré-remplissage depuis l'URL (?profile=artisan|delegataire|oblige|apporteur)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const preset = findProfile(params.get('profile'));
    if (preset) setProfile(preset);
  }, []);

  const updateField = <K extends keyof FormState>(key: K) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value =
        e.target instanceof HTMLInputElement && e.target.type === 'checkbox'
          ? e.target.checked
          : e.target.value;
      setForm((prev) => ({ ...prev, [key]: value as FormState[K] }));
    };

  const updateSpecific = (name: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, specific: { ...prev.specific, [name]: value } }));
  };

  const handleSelectProfile = (p: PartnerProfile) => {
    setProfile(p);
    setForm((prev) => ({ ...prev, specific: {} }));
    requestAnimationFrame(() => {
      document
        .getElementById('candidature-form')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    if (!profile) {
      toast.error('Profil partenaire requis', {
        description: 'Sélectionnez d’abord le mode de partenariat (étape 1).',
      });
      return;
    }
    if (!form.email.trim()) {
      toast.error('Email requis');
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error('Nom et prénom requis');
      return;
    }
    if (!form.company.trim()) {
      toast.error('Structure / société requise');
      return;
    }
    if (!form.consentRgpd) {
      toast.error('Consentement RGPD requis', {
        description: 'Cochez la case d’acceptation de notre politique de confidentialité.',
      });
      return;
    }

    const specificLines = profile.specificFields
      .map((f) => {
        const v = form.specific[f.name]?.trim();
        return v ? `${f.label} : ${v}` : null;
      })
      .filter(Boolean) as string[];

    const messageWithContext = [
      `[CANDIDATURE PARTENAIRE — ${profile.label.toUpperCase()}]`,
      form.role.trim() ? `Fonction : ${form.role.trim()}` : null,
      `${profile.companyLabel} : ${form.company.trim()}`,
      ...specificLines,
      form.message.trim() ? `\nMessage :\n${form.message.trim()}` : null,
    ]
      .filter(Boolean)
      .join('\n');

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      message: messageWithContext,
      consentMarketing: form.consentMarketing,
      source: 'form' as const,
      segment: profile.segment,
      intent: profile.intent,
    };

    setSubmitting(true);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Erreur ${res.status}`);
      }
      const data = (await res.json()) as { id: string };
      setSuccess({ id: data.id });
      toast.success('Candidature envoyée', {
        description: 'Un référent partenaires vous rappelle sous 24 h ouvrées.',
      });
    } catch (err) {
      toast.error('Envoi impossible', {
        description: err instanceof Error ? err.message : 'Réessayez dans un instant.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const headerCopy = useMemo(() => {
    if (!profile) {
      return {
        eyebrow: 'Étape 1 — Choisissez votre profil',
        title: 'Candidater au programme partenaires.',
        description:
          "Sélectionnez d'abord le mode de partenariat qui vous correspond. Le formulaire s'adapte ensuite aux informations utiles pour qualifier votre demande.",
      };
    }
    return {
      eyebrow: profile.formEyebrow,
      title: `${profile.label} — votre candidature.`,
      description:
        'Précisez votre profil et votre contexte. Un référent commercial vous recontacte sous 24 h ouvrées avec une proposition de cadrage adaptée.',
    };
  }, [profile]);

  return (
    <div className="bg-paper">
      {/* ─────────────── HERO ─────────────── */}
      <section className="relative overflow-hidden border-b border-stone-200/70 bg-stone-50/60 py-14 md:py-16">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 right-1/4 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl"
        />
        <Container className="relative max-w-4xl">
          <Reveal>
            <a
              href={ROUTES.devenirPartenaire}
              className="inline-flex items-center gap-1 text-[12.5px] font-medium text-stone-500 underline-offset-2 hover:text-ink hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              Retour à la présentation des partenariats
            </a>
            <p className="eyebrow mt-4">
              <Sparkles className="h-3 w-3 text-brand-600" strokeWidth={2} />
              <span>Candidature partenaires · 24 h ouvrées</span>
            </p>
            <h1 className="mt-4 font-display text-display-2 font-bold tracking-tight text-balance text-ink">
              {headerCopy.title.split(' ').slice(0, -1).join(' ')}{' '}
              <span className="text-gradient-soft">
                {headerCopy.title.split(' ').slice(-1)[0]}
              </span>
            </h1>
            <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-stone-600">
              {headerCopy.description}
            </p>
          </Reveal>
          <Reveal delay={0.08} className="mt-6 flex flex-wrap gap-2">
            {[
              'Sans engagement',
              'Sans exclusivité',
              'CGV et mandat publiés en amont',
              'RGPD strict',
            ].map((cert) => (
              <Badge key={cert} variant="default" size="md" className="gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-brand-600" strokeWidth={2.4} />
                {cert}
              </Badge>
            ))}
          </Reveal>
        </Container>
      </section>

      {/* ─────────────── SÉLECTEUR DE PROFIL ─────────────── */}
      <section className="bg-paper py-14">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow">Étape 1 · Mode de partenariat</p>
              <h2 className="mt-3 h-section">
                {profile ? 'Modifier votre profil partenaire.' : 'Quel partenariat vous intéresse ?'}
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                Choisissez la carte qui correspond à votre activité. Le formulaire s'adapte
                aux informations utiles à votre profil.
              </p>
            </div>
          </Reveal>

          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PARTNERS.map((p, i) => {
              const Icon = p.icon;
              const tone = TONE_STYLES[p.tone];
              const active = profile?.id === p.id;
              return (
                <Reveal key={p.id} delay={i * 0.04}>
                  <button
                    type="button"
                    onClick={() => handleSelectProfile(p)}
                    aria-pressed={active}
                    className={cn(
                      'group relative flex h-full w-full flex-col items-start gap-3 rounded-xl border bg-paper p-4 text-left transition-all duration-200 ease-out',
                      'hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2',
                      active
                        ? cn('border-transparent ring-2 ring-offset-2', tone.ringActive, tone.bg)
                        : 'border-stone-200',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset transition-colors',
                        tone.iconBg,
                        tone.ring,
                      )}
                    >
                      <Icon className={cn('h-4 w-4', tone.iconText)} strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="font-display text-[14.5px] font-semibold text-ink">
                          {p.label}
                        </p>
                        {p.highlight && (
                          <Badge variant="accent" size="sm" className="px-1.5 py-0">
                            Recommandé
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-[12.5px] leading-snug text-stone-500">
                        {p.hint}
                      </p>
                    </div>
                    {active && (
                      <div
                        className={cn(
                          'absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full',
                          tone.iconBg,
                          tone.iconText,
                        )}
                        aria-hidden
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </div>
                    )}
                  </button>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ─────────────── FORMULAIRE + SIDEBAR ─────────────── */}
      <section
        id="candidature-form"
        className="border-t border-stone-200/70 bg-stone-50/40 py-16 scroll-mt-20"
      >
        <Container className="max-w-5xl">
          <div className="grid gap-8 lg:grid-cols-[1.45fr_1fr]">
            {/* FORM */}
            <Reveal>
              <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-soft sm:p-8">
                {success ? (
                  <SuccessPanel
                    leadId={success.id}
                    profile={profile}
                    onReset={() => {
                      setSuccess(null);
                      setForm(INITIAL_FORM);
                    }}
                  />
                ) : (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="eyebrow">Étape 2 · Vos informations</p>
                        <h2 className="mt-2 font-display text-h-pillar font-bold text-ink">
                          {profile ? profile.formEyebrow : 'Sélectionnez d’abord un profil ci-dessus'}
                        </h2>
                      </div>
                      {profile && (
                        <button
                          type="button"
                          onClick={() => setProfile(null)}
                          className="inline-flex items-center gap-1 text-[12.5px] font-medium text-stone-500 underline-offset-2 hover:text-ink hover:underline"
                        >
                          Changer de profil
                        </button>
                      )}
                    </div>

                    {profile && (
                      <div className="mt-4 flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" size="sm">
                          <profile.icon className="h-3 w-3" strokeWidth={2} />
                          {profile.label}
                        </Badge>
                        <Badge variant="secondary" size="sm">
                          {profile.pill}
                        </Badge>
                      </div>
                    )}

                    {!profile && (
                      <div className="mt-6 rounded-xl border border-dashed border-stone-300 bg-stone-50/60 p-6 text-center">
                        <p className="text-[14px] text-stone-500">
                          Choisissez l'une des 4 cartes ci-dessus pour activer le formulaire.
                        </p>
                      </div>
                    )}

                    {profile && (
                      <form
                        className="mt-6 grid gap-4 sm:grid-cols-2"
                        onSubmit={handleSubmit}
                        noValidate
                      >
                        {/* Coordonnées du contact */}
                        <FieldText
                          label="Prénom"
                          name="firstName"
                          autoComplete="given-name"
                          required
                          value={form.firstName}
                          onChange={updateField('firstName')}
                          disabled={submitting}
                        />
                        <FieldText
                          label="Nom"
                          name="lastName"
                          autoComplete="family-name"
                          required
                          value={form.lastName}
                          onChange={updateField('lastName')}
                          disabled={submitting}
                        />
                        <FieldText
                          label="Email professionnel"
                          type="email"
                          name="email"
                          autoComplete="email"
                          required
                          className="sm:col-span-2"
                          value={form.email}
                          onChange={updateField('email')}
                          disabled={submitting}
                        />
                        <FieldText
                          label="Téléphone direct"
                          type="tel"
                          name="phone"
                          autoComplete="tel"
                          hint="Pour un rappel sous 24 h ouvrées."
                          value={form.phone}
                          onChange={updateField('phone')}
                          disabled={submitting}
                        />
                        <FieldText
                          label="Fonction"
                          name="role"
                          autoComplete="organization-title"
                          placeholder="Dirigeant, responsable développement, gérant…"
                          hint="Optionnel — facilite la mise en relation."
                          value={form.role}
                          onChange={updateField('role')}
                          disabled={submitting}
                        />
                        <FieldText
                          label={profile.companyLabel}
                          name="company"
                          autoComplete="organization"
                          required
                          className="sm:col-span-2"
                          value={form.company}
                          onChange={updateField('company')}
                          disabled={submitting}
                        />

                        {/* Champs spécifiques au profil */}
                        <div className="sm:col-span-2 mt-2 rounded-xl border border-stone-200 bg-stone-50/40 p-5">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                            Informations spécifiques · {profile.label}
                          </p>
                          <div className="mt-4 grid gap-4 sm:grid-cols-2">
                            {profile.specificFields.map((field) => (
                              <FieldText
                                key={field.name}
                                label={
                                  field.optional ? `${field.label} (optionnel)` : field.label
                                }
                                name={field.name}
                                type={field.type ?? 'text'}
                                placeholder={field.placeholder}
                                hint={field.hint}
                                required={!field.optional}
                                value={form.specific[field.name] ?? ''}
                                onChange={updateSpecific(field.name)}
                                disabled={submitting}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Message libre */}
                        <div className="sm:col-span-2">
                          <Label
                            htmlFor="candidature-message"
                            className="font-medium text-ink"
                          >
                            Votre contexte (optionnel)
                          </Label>
                          <textarea
                            id="candidature-message"
                            name="message"
                            rows={5}
                            value={form.message}
                            onChange={updateField('message')}
                            disabled={submitting}
                            placeholder={profile.messagePlaceholder}
                            className="mt-1.5 block w-full rounded-xl border border-stone-200 bg-paper px-3 py-2.5 text-[14px] leading-relaxed text-ink placeholder:text-stone-400 transition-colors focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                          />
                        </div>

                        {/* Consentement RGPD obligatoire */}
                        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-stone-200 bg-stone-50/40 p-3 text-[12.5px] text-stone-600 sm:col-span-2">
                          <input
                            type="checkbox"
                            name="consentRgpd"
                            checked={form.consentRgpd}
                            onChange={updateField('consentRgpd')}
                            disabled={submitting}
                            required
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 text-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500/30"
                          />
                          <span className="leading-relaxed">
                            J'accepte que mes données soient traitées par Solution Éco
                            France pour étudier ma candidature partenaire, conformément à la{' '}
                            <a
                              href={ROUTES.legal.confidentialite}
                              className="font-semibold text-brand-700 underline-offset-2 hover:underline"
                            >
                              politique de confidentialité
                            </a>
                            .{' '}
                            <span className="font-semibold text-brand-700">*</span>
                          </span>
                        </label>

                        {/* Consentement marketing optionnel */}
                        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-stone-200 bg-paper p-3 text-[12.5px] text-stone-600 sm:col-span-2">
                          <input
                            type="checkbox"
                            name="consentMarketing"
                            checked={form.consentMarketing}
                            onChange={updateField('consentMarketing')}
                            disabled={submitting}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 text-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500/30"
                          />
                          <span className="leading-relaxed">
                            J'accepte de recevoir occasionnellement la veille SEF dédiée
                            partenaires (évolutions CEE, marché des primes, jurisprudence).
                            Désabonnement en 1 clic.
                          </span>
                        </label>

                        <Button
                          type="submit"
                          variant="brand"
                          size="xl"
                          disabled={submitting}
                          aria-busy={submitting}
                          className="sm:col-span-2"
                        >
                          {submitting ? (
                            <>
                              <Spinner /> Envoi en cours…
                            </>
                          ) : (
                            <>
                              Envoyer ma candidature
                              <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                            </>
                          )}
                        </Button>

                        <p className="mt-1 text-center text-[11.5px] text-stone-500 sm:col-span-2">
                          <ShieldCheck
                            className="mr-1 inline h-3 w-3 text-brand-600"
                            strokeWidth={2}
                          />
                          {profile.footnote}
                        </p>
                      </form>
                    )}
                  </>
                )}
              </div>
            </Reveal>

            {/* SIDEBAR */}
            <aside className="space-y-5">
              <Reveal>
                <div className="rounded-2xl border border-stone-200 bg-paper p-6">
                  <p className="eyebrow">Canal direct partenaires</p>
                  <h3 className="mt-2 font-display text-h-card-lg font-semibold text-ink">
                    Préférez nous écrire directement ?
                  </h3>
                  <ul className="mt-4 space-y-2">
                    <li>
                      <a
                        href="mailto:partenariats@solutionecofrance.fr"
                        className="group flex items-start gap-3 rounded-xl border border-stone-200/70 bg-stone-50/40 p-3 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
                      >
                        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                          <Mail className="h-3.5 w-3.5 text-brand-700" strokeWidth={1.8} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                            Email partenariats
                          </span>
                          <span className="block truncate text-[13.5px] font-medium text-ink">
                            partenariats@solutionecofrance.fr
                          </span>
                          <span className="block text-[11.5px] text-stone-500">
                            Réponse sous 24 h ouvrées
                          </span>
                        </span>
                        <ArrowUpRight
                          className="mt-0.5 h-3.5 w-3.5 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-ink"
                          strokeWidth={2}
                        />
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.linkedin.com/company/solution-eco-france"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-start gap-3 rounded-xl border border-stone-200/70 bg-stone-50/40 p-3 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
                      >
                        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                          <Linkedin
                            className="h-3.5 w-3.5 text-brand-700"
                            strokeWidth={1.8}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                            LinkedIn
                          </span>
                          <span className="block truncate text-[13.5px] font-medium text-ink">
                            Solution Éco France
                          </span>
                          <span className="block text-[11.5px] text-stone-500">
                            Échanges directs équipe partenariats
                          </span>
                        </span>
                        <ArrowUpRight
                          className="mt-0.5 h-3.5 w-3.5 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-ink"
                          strokeWidth={2}
                        />
                      </a>
                    </li>
                    <li>
                      <a
                        href={ROUTES.legal.cgv}
                        className="group flex items-start gap-3 rounded-xl border border-stone-200/70 bg-stone-50/40 p-3 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
                      >
                        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                          <FileBadge
                            className="h-3.5 w-3.5 text-brand-700"
                            strokeWidth={1.8}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                            CGV & mandats
                          </span>
                          <span className="block truncate text-[13.5px] font-medium text-ink">
                            Notre modèle économique
                          </span>
                          <span className="block text-[11.5px] text-stone-500">
                            Modèle transparent · sans frais cachés
                          </span>
                        </span>
                        <ArrowUpRight
                          className="mt-0.5 h-3.5 w-3.5 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-ink"
                          strokeWidth={2}
                        />
                      </a>
                    </li>
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={0.05}>
                <div className="rounded-2xl border border-stone-200 bg-paper p-6">
                  <div className="flex items-center gap-2">
                    <span aria-hidden className="relative inline-flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-glow-pulse rounded-full bg-brand-500/60" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
                    </span>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Ce qui se passe ensuite
                    </p>
                  </div>
                  <ol className="mt-4 space-y-3">
                    {NEXT_STEPS.map((step, i) => (
                      <li key={step.n} className="flex gap-3">
                        <div className="flex h-10 w-12 shrink-0 items-center justify-center rounded-lg bg-brand-50 font-display text-[11px] font-bold text-brand-700">
                          {step.n}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13.5px] font-semibold leading-tight text-ink">
                            {i + 1}. {step.title}
                          </p>
                          <p className="mt-0.5 text-[12px] leading-relaxed text-stone-500">
                            {step.desc}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] text-stone-500">
                    <Clock className="h-3 w-3 text-brand-600" strokeWidth={2} />
                    Délais moyens observés en 2025.
                  </p>
                </div>
              </Reveal>
            </aside>
          </div>
        </Container>
      </section>

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          {
            name: 'Devenir partenaire',
            url: 'https://solutionecofrance.fr/devenir-partenaire',
          },
          {
            name: 'Candidater',
            url: 'https://solutionecofrance.fr/devenir-partenaire/candidater',
          },
        ]}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// Sous-composants
// ════════════════════════════════════════════════════════════════

interface FieldTextProps {
  label: string;
  name: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  hint?: string;
  placeholder?: string;
  className?: string;
}

function FieldText({
  label,
  name,
  type = 'text',
  autoComplete,
  required,
  value,
  onChange,
  disabled,
  hint,
  placeholder,
  className,
}: FieldTextProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label
        htmlFor={`field-${name}`}
        className="flex items-center gap-1 text-[13px] font-medium text-ink"
      >
        {label}
        {required && <span className="text-brand-600">*</span>}
      </Label>
      <input
        id={`field-${name}`}
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className="h-10 w-full rounded-xl border border-stone-200 bg-paper px-3 text-[14px] text-ink placeholder:text-stone-400 transition-colors focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60"
      />
      {hint && <p className="text-[11.5px] leading-snug text-stone-500">{hint}</p>}
    </div>
  );
}

function Label({
  htmlFor,
  className,
  children,
}: {
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className={className}>
      {children}
    </label>
  );
}

function Spinner() {
  return (
    <svg aria-hidden className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="4"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface SuccessPanelProps {
  leadId: string;
  profile: PartnerProfile | null;
  onReset: () => void;
}

function SuccessPanel({ leadId, profile, onReset }: SuccessPanelProps) {
  return (
    <div className="relative">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100">
          <CheckCircle2 className="h-5 w-5 text-brand-700" strokeWidth={2} />
        </div>
        <div>
          <p className="font-display text-h-pillar font-bold text-ink">
            Candidature envoyée.
          </p>
          <p className="text-[13px] text-stone-500">
            Référence #{leadId.slice(0, 8).toUpperCase()} · réponse sous 24 h ouvrées
          </p>
        </div>
      </div>

      <p className="mt-5 text-[14.5px] leading-relaxed text-stone-600">
        Merci de votre candidature{profile ? ` au programme « ${profile.label} »` : ''}.
        Un référent commercial vous recontacte sous 24 heures ouvrées (le plus souvent
        dans la demi-journée) pour qualifier votre profil et organiser un premier échange.
      </p>

      <ul className="mt-5 space-y-2">
        <li>
          <a
            href={ROUTES.legal.cgv}
            className="group flex items-start justify-between gap-3 rounded-xl border border-stone-200 bg-paper p-4 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-elevated"
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                <FileBadge className="h-4 w-4 text-brand-700" strokeWidth={1.8} />
              </span>
              <div>
                <p className="font-display text-[14px] font-semibold text-ink">
                  Préparer la rencontre : lire nos CGV
                </p>
                <p className="mt-0.5 text-[12.5px] leading-snug text-stone-500">
                  Notre modèle économique transparent — sans frais cachés.
                </p>
              </div>
            </div>
            <ArrowUpRight
              className="mt-1 h-4 w-4 shrink-0 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-brand-700"
              strokeWidth={2}
            />
          </a>
        </li>
        <li>
          <a
            href={ROUTES.devenirPartenaire}
            className="group flex items-start justify-between gap-3 rounded-xl border border-stone-200 bg-paper p-4 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-elevated"
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                <Sparkles className="h-4 w-4 text-brand-700" strokeWidth={1.8} />
              </span>
              <div>
                <p className="font-display text-[14px] font-semibold text-ink">
                  Revoir les 4 modes de partenariat
                </p>
                <p className="mt-0.5 text-[12.5px] leading-snug text-stone-500">
                  Toutes les modalités, étapes et engagements réciproques.
                </p>
              </div>
            </div>
            <ArrowUpRight
              className="mt-1 h-4 w-4 shrink-0 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-brand-700"
              strokeWidth={2}
            />
          </a>
        </li>
      </ul>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="md" onClick={onReset}>
          Envoyer une autre candidature
        </Button>
        <a
          href="mailto:partenariats@solutionecofrance.fr"
          className="inline-flex items-center gap-1 text-[13px] font-medium text-stone-500 underline-offset-2 hover:text-ink hover:underline"
        >
          <Headphones className="h-3.5 w-3.5" strokeWidth={2} />
          Contact direct
        </a>
      </div>
    </div>
  );
}
